import { NgForOf, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { UserService } from '@vendasta/business-center';
import { emailValidator } from '@vendasta/forms';
import { GalaxyCheckboxModule } from '@vendasta/galaxy/checkbox';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { AppConfigService } from '../../app-config.service';
import { EmailLocale, SELECT_WELCOME_EMAIL_LOCALES } from '../../constants';
import { PartnerService } from '../../core/partner.service';
import { User } from '../../core/user';
import { VbcApiService } from '../vbc-api.service';
import { PreviewEmailModalComponent } from './preview-email/preview-email-modal.component';

@Component({
  selector: 'app-create-user-form',
  templateUrl: './create-user-form.component.html',
  styleUrls: ['./create-user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    GalaxyCheckboxModule,
    GalaxyInputModule,
    GalaxySnackbarModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    NgForOf,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class CreateUserFormComponent {
  @Input() accountGroupId = '';
  @Output() userCreated: EventEmitter<User> = new EventEmitter();
  protected readonly emailLanguages = SELECT_WELCOME_EMAIL_LOCALES;
  returnUrl: string;
  loginUrl: string;

  private _isCreatingUser$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _isCreatingUser$: Observable<boolean> = this._isCreatingUser$$.asObservable();

  readonly form = new FormGroup({
    first_name: new FormControl(''),
    last_name: new FormControl(''),
    greeting_name: new FormControl(''),
    user_email: new FormControl('', [Validators.required, emailValidator]),
    phone: new FormControl(''),
    languages: new FormControl<EmailLocale>({ value: 'en', label: 'English' }),
    send_welcome_email: new FormControl(false),
    custom_welcome_message: new FormControl(''),
  });

  constructor(
    private snackbarService: SnackbarService,
    public filterModal: MatDialog,
    private vbcApiService: VbcApiService,
    private appConfigService: AppConfigService,
    private bcUserService: UserService,
    public partnerService: PartnerService,
  ) {}

  public isCreatingUser(): Observable<boolean> {
    return this._isCreatingUser$;
  }

  async createUser(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAsDirty();
      this.snackbarService.openErrorSnack('CREATE_BUSSINESS_USER_PAGE.CREATE_USER_INVALID_FORM');
      return;
    }

    this._isCreatingUser$$.next(true);

    const createUserRequest = {
      partnerId: this.appConfigService.config.partnerId,
      accountGroupId: this.accountGroupId,
      firstName: this.form.value.first_name || '',
      lastName: this.form.value.last_name || '',
      greetingName: this.form.value.greeting_name || '',
      email: this.form.value.user_email,
      phone: this.form.value.phone,
      welcomeMessageFlag: this.form.value.send_welcome_email || false,
      emailLocale: this.form.value.languages,
      welcomeMessage: this.form.value.custom_welcome_message || '',
    };

    try {
      const user = await firstValueFrom(
        this.vbcApiService.createUser(
          createUserRequest.partnerId,
          createUserRequest.accountGroupId,
          createUserRequest.firstName,
          createUserRequest.lastName,
          createUserRequest.greetingName,
          createUserRequest.email.toLowerCase(),
          createUserRequest.phone,
          createUserRequest.welcomeMessageFlag.toString(),
          createUserRequest.emailLocale.value,
          createUserRequest.welcomeMessage,
        ),
      );
      this.userCreated.emit(user);
      this.snackbarService.openSuccessSnack('CREATE_BUSSINESS_USER_PAGE.CREATE_USER_SUCCESS');
    } catch (err) {
      let errTranslationId = 'CREATE_BUSSINESS_USER_PAGE.CREATE_USER_ERROR';
      if (err?.data?.message?.includes('LimitError:')) {
        errTranslationId = 'CREATE_BUSSINESS_USER_PAGE.CREATE_USER_LIMIT_ERROR';
      } else if (err?.data?.message?.includes('already exists')) {
        errTranslationId = 'CREATE_BUSSINESS_USER_PAGE.CREATE_USER_ALREADY_EXISTS_ERROR';
      }
      this.snackbarService.openErrorSnack(errTranslationId);
    } finally {
      this._isCreatingUser$$.next(false);
    }
  }

  openPreviewEmail(): void {
    this.partnerService.getBusinessCenterHostname().subscribe((loginUrl) => {
      this.loginUrl = loginUrl + '/login/';
    });
    this.filterModal.open(PreviewEmailModalComponent, {
      data: {
        template: this.bcUserService.getWelcomeEmailTemplate(
          this.partnerService.partnerId,
          this.form.value.greeting_name || this.form.value.first_name || '',
          this.loginUrl,
          '',
          this.form.value.custom_welcome_message || '',
          this.form.value.languages.value,
        ),
      },
      width: '620px',
      height: '80vh',
      maxHeight: '800px',
    });
  }
}
